import { useRef } from "react";

/**
 * Dev-only diagnostic: warns once per render when a prop that the consumer is
 * expected to memoize was given a fresh reference. Useful behind a `React.memo`
 * — if these props are unstable, the memo bails out on every parent render and
 * the memo boundary is effectively a no-op.
 *
 * No-op in production (the call site is tree-shaken because the body is
 * gated on `process.env.NODE_ENV !== "production"`).
 *
 * @param componentName - shown in the warning header
 * @param props - record of prop name → current value; identity is compared
 */
export function useDevWarnUnstableProps(
  componentName: string,
  props: Record<string, unknown>
): void {
  const previousRef = useRef<Record<string, unknown> | null>(null);

  if (process.env.NODE_ENV === "production") return;

  const previous = previousRef.current;
  previousRef.current = props;

  if (previous === null) return;

  const changed: string[] = [];
  for (const key of Object.keys(props)) {
    if (previous[key] !== props[key]) {
      changed.push(key);
    }
  }

  if (changed.length > 0) {
    console.warn(
      `[${componentName}] memo bypassed: prop identity changed for ${changed
        .map((k) => `\`${k}\``)
        .join(
          ", "
        )}. Stabilize these in the parent (useMemo / useCallback / module-level constants) or the React.memo wrapper is wasted work.`
    );
  }
}
